// Foundations
import { Injectable } from '@angular/core';

// rxjs
import { forkJoin, throwError } from 'rxjs';
import {catchError, delay, filter, finalize, map, mergeMap, repeatWhen, switchMap, take, tap} from 'rxjs/operators';

// Services
import { AuthService } from '../auth/auth.service';
import { HttpService } from './http.service';
import { LoadingIndicatorService } from './loading-indicator.service';
import {
  AccountTermPolicyForm,
  AddAccountTermPolicyFormGQL, CreateEmbeddedSignatureRequestWithTemplateGQL,
  GetAccountDetailsGQL,
  GetAccountTermPolicyFormsGQL,
  GetPreferredLocationGQL,
  GetTermPolicyFormsGQL, GetSignatureRequestFilesGQL, GetSignatureRequestGQL,
  TermPolicyForm,
} from 'src/app/services/graphql.service';

// Extensions
import HelloSign from 'hellosign-embedded';

// ENV
import { environment } from '../../environments/environment';

const {
  helloSign: { config: HELLO_SIGN_CONFIG },
} = environment;

@Injectable({
  providedIn: 'root',
})
export class HelloSignService {
  requiredTermPolicyForm: Partial<TermPolicyForm>;
  signedTermPolicyForm: Partial<AccountTermPolicyForm>;

  inCheckingSigningStatus = false;
  signingCompleted = false;

  private client = new HelloSign(HELLO_SIGN_CONFIG);
  private accountTermPolicyForms: Partial<AccountTermPolicyForm>[];
  private templateId: string;
  private termPolicyFormId: string;
  private signatureRequestId: string;

  constructor(
    private authService: AuthService,
    private httpService: HttpService,
    private loadingIndicatorService: LoadingIndicatorService,
    private getPreferredLocationQuery: GetPreferredLocationGQL,
    private getAccountDetailsQuery: GetAccountDetailsGQL,
    private getTermPolicyFormsQuery: GetTermPolicyFormsGQL,
    private getAccountTermPolicyFormsQuery: GetAccountTermPolicyFormsGQL,
    private addAccountTermPolicyFormMutation: AddAccountTermPolicyFormGQL,
    private getSignatureRequest: GetSignatureRequestGQL,
    private getSignatureRequestFiles: GetSignatureRequestFilesGQL,
    private createEmbeddedSignatureRequestWithTemplate: CreateEmbeddedSignatureRequestWithTemplateGQL
  ) {
    // When the user has finished the embedded signature request flow without cancelling,
    // record release form and signature request IDs in GraphQL
    this.client.on('finish', () => {
      //
      this._handleSigningCompletion();
    });
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  createSignatureRequest() {
    this.loadingIndicatorService.show();
    this.authService.accountId$
      .pipe(
        filter((accountId) => !!accountId),
        take(1),
        mergeMap((accountId) =>
          this.getAccountDetailsQuery.fetch({ accountId })
        ),
        map((res) => res.data.getAccount),
        mergeMap((account) => {
          const { firstName, lastName, email } = account;
          return this.createEmbeddedSignatureRequestWithTemplate.mutate({
            termPolicyFormId: this.termPolicyFormId,
            embeddedInput: {
              testMode : HELLO_SIGN_CONFIG.testMode,
              clientId:  HELLO_SIGN_CONFIG.clientId,
              signers: [{
                roleName: 'CpAppUser',
                name: firstName + ' ' + lastName,
                emailAddress: email
              }]
            }
          });
        }),
        finalize(() => this.loadingIndicatorService.hide())
      )
      .subscribe((signUrl) => {
        // Open Embedded Sign URL in iFrame
        this.signatureRequestId = signUrl.data.createEmbeddedSignatureRequestWithTemplate.signatureRequestId;
        this.client.open(signUrl.data.createEmbeddedSignatureRequestWithTemplate.embeddedSignUrl);
      });
  }

  getExistingSignature() {
    this.getSignatureRequestFiles.fetch({accountTermPolicyFormId: this.termPolicyFormId})
    .subscribe((fileUrl) => window.open(fileUrl.data.getSignatureRequestFiles, '_blank'));
  }

  hasCountryTermPolicyForm(country: 'us' | 'ca') {
    return (
      !!this.signedTermPolicyForm &&
      this.signedTermPolicyForm.termPolicyForm.geoCountry.countryIso.toLowerCase() ===
        country
    );
  }

  fetchForms() {
    /*
      Try to fetch the form.
      Use account's preferred location info to help decide which "form" to use.
    */
    forkJoin([
      // Get required release form
      this.authService.accountId$.pipe(
        filter((accountId) => !!accountId),
        take(1),
        mergeMap((accountId) =>
          this.getPreferredLocationQuery.fetch({ accountId })
        ),
        map((res) => res.data.getPreferredLocation.id),
        mergeMap((franchiseId) =>
          this.getTermPolicyFormsQuery.fetch({ franchiseId })
        ),
        // TODO - Only use the "first" record if returning multiple
        tap((res) => (this.requiredTermPolicyForm = res.data.getTermPolicyForms[0]))
      ),
      // Get account release forms (release forms signed by user)
      this.authService.accountId$.pipe(
        filter((accountId) => !!accountId),
        take(1),
        mergeMap((accountId) =>
          this.getAccountTermPolicyFormsQuery.fetch({ accountId })
        ),
        tap(
          (res) => (this.accountTermPolicyForms = res.data.getAccountTermPolicyForms)
        )
      ),
    ]).subscribe(() => {
      this.signedTermPolicyForm = this.accountTermPolicyForms.find(
        (accountTermPolicyForm) =>
          accountTermPolicyForm.termPolicyForm.id === this.requiredTermPolicyForm.id
      );
      if (this.signedTermPolicyForm) {
        this.signatureRequestId = this.signedTermPolicyForm.documentId;
        this.termPolicyFormId = this.signedTermPolicyForm.id;
        this._checkSigningStatus().subscribe();
      } else {
        // No signed form, use default value
        this.signatureRequestId = undefined;
        this.signingCompleted = false;
        this.templateId = this.requiredTermPolicyForm.templateId;
        this.termPolicyFormId = this.requiredTermPolicyForm.id;
      }
    });
  }

  // #region -> Private Method <-
  // ----------
  private _handleSigningCompletion(): void {
    //
    this.loadingIndicatorService.show();

    // Also enable the "status flag"
    this.inCheckingSigningStatus = true;

    // Check and wait for the "signing" status change and then update the record.
    this.getSignatureRequest.fetch({documentId: this.signatureRequestId })
      .pipe(
        repeatWhen((completed) => completed.pipe(
          delay(3000),
          take(5) // # of Repeats
          )),
        filter((completionStatus) => {
          return completionStatus.data.getSignatureRequest.isComplete === true;
        }),
        take(1),
        switchMap((completionStatus) => {
          // Update the "signing" status
          this.signingCompleted = completionStatus.data.getSignatureRequest.isComplete;

          // Also add a record to the backend when the "signing" is completed.
          if (completionStatus.data.getSignatureRequest.isComplete ) {
            return this.authService.accountId$
              .pipe(
                filter((accountId) => !!accountId),
                take(1),
                mergeMap((accountId) =>
                  this.addAccountTermPolicyFormMutation.mutate({
                    accountTermPolicyForm: {
                      account: accountId,
                      termPolicyForm: this.termPolicyFormId,
                      documentId: this.signatureRequestId,
                    },
                    // Also indicate a file upload to S3 bucket
                    uploadSignedFileToS3: true
                  })
                ),
                map((res) => res.data.addAccountTermPolicyForm)
              )
          } else {
            // Throw an error
            throw new Error('The doc signing is not completed.')
          }
        }),
        catchError((error) => {
          return throwError(() => error);
        }),
        finalize(() => {
          this.loadingIndicatorService.hide();
          this.inCheckingSigningStatus = false;
        })
      ).subscribe((signedTermPolicyForm) => {
        // Updates
        if (signedTermPolicyForm) {
          this.signedTermPolicyForm = signedTermPolicyForm;
          this.termPolicyFormId = signedTermPolicyForm.id;
        }
      });
  }

  private _checkSigningStatus() {
    // Flag - if it is in checking the signing status
    this.inCheckingSigningStatus = true;


    return this.getSignatureRequest.fetch({documentId: this.signatureRequestId})
      .pipe(
        repeatWhen((completed) => completed.pipe(
          delay(3000),
          take(5) // # of Repeats
          )),
        filter((completionStatus) => {
          return completionStatus.data.getSignatureRequest.isComplete === true;
        }),
        take(1),
        tap((completionStatus) => {
          if (completionStatus.data.getSignatureRequest.isComplete ) {
            this.signingCompleted = completionStatus.data.getSignatureRequest.isComplete ;
            this.inCheckingSigningStatus = false;
          }
        })
      );
  }

  // ----------
  // #endregion

  /////////////////////////////
}
